import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

/**
 * JWT Interceptor — attaches the Bearer token to outgoing requests
 * and automatically attempts a token refresh on 401 responses.
 */
export const jwtInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const token = authService.getToken();

    // Don't attach token to auth endpoints (login, register, refresh-token)
    const isAuthEndpoint = req.url.includes('/Auth/login')
        || req.url.includes('/Auth/register')
        || req.url.includes('/Auth/refresh-token')
        || req.url.includes('/Auth/forgot-password')
        || req.url.includes('/Auth/reset-password');

    let authReq = req;
    if (token && !isAuthEndpoint) {
        authReq = req.clone({
            setHeaders: { Authorization: `Bearer ${token}` }
        });
    }

    return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
            // Only attempt refresh on 401, when not already on an auth endpoint
            if (error.status === 401 && !isAuthEndpoint && authService.hasSession()) {
                return authService.refreshToken().pipe(
                    switchMap(response => {
                        if (response?.success && response.data) {
                            // Retry the original request with the new token
                            const retryReq = req.clone({
                                setHeaders: { Authorization: `Bearer ${response.data.token}` }
                            });
                            return next(retryReq);
                        }
                        // Refresh succeeded but no data — force logout
                        authService.logout().subscribe();
                        router.navigate(['/auth/login']);
                        return throwError(() => error);
                    }),
                    catchError(refreshError => {
                        // Refresh itself failed — session is dead
                        authService.logout().subscribe();
                        router.navigate(['/auth/login']);
                        return throwError(() => refreshError);
                    })
                );
            }

            return throwError(() => error);
        })
    );
};
